'use client';

import type React from 'react';
import { useEffect, useState } from 'react';
import {
  Container,
  Paper,
  Typography,
  useTheme,
  Button,
  Skeleton,
  Grid2,
  Box,
} from '@mui/material';
import { AppBar } from '../../components';
import KpiCard from '../../components/KpiCard';
import { useParams } from 'react-router-dom';
import { useAuth, useCompanyUser } from '../../contexts';
import { usePayments } from '../../contexts/payment';
import WalletOutlinedIcon from '@mui/icons-material/WalletOutlined';
import InsightsIcon from '@mui/icons-material/Insights';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import TuneIcon from '@mui/icons-material/Tune';
import ReceiptIcon from '@mui/icons-material/Receipt';

import AddIcon from '@mui/icons-material/Add';
import {
  ContentGrid,
  MainContent,
  SidebarContent,
  CardsSection,
  ChartSection,
  ActionButtonsContainer,
} from './styles';
import type { ITransactionViewModel } from 'viewModels';
import { TransactionSidebar } from 'components/TransactionSideBar';
import { Empty } from 'antd';
import { DualAxes } from '@ant-design/plots';
import AddCreditModal from 'components/Modal/AddCreditModal';
import { TypeUser } from 'types/enums';
import TransactionDetailsModal from 'components/Modal/TransactionDetailsModal';
import InvoiceModal from 'components/Modal/InvoiceModal';
import InvoiceList from 'components/InvoiceList';
import { InvoiceStatus } from 'types/enums';
import { TransactionType } from 'types/enums';

const Balance: React.FC = () => {
  const { userId } = useParams<{ userId?: string }>();
  const { user } = useAuth();
  const isAdmin = user?.typeUser === TypeUser.ADM;
  const isAdminView = isAdmin && !!userId;

  // Estados existentes
  const [showCreditModal, setShowCreditModal] = useState(false);
  const [showTransactionDetails, setShowTransactionDetails] =
    useState<boolean>(false);
  const [currentTransaction, setCurrentTransaction] =
    useState<ITransactionViewModel>();

  const [showInvoiceModal, setShowInvoiceModal] = useState(false);

  const payments = usePayments();
  const {
    companyUsers,
    loadingUserHistory,
    loadingUserTransactions,
    addUserCredit,
    loadingAddCredit,
    setSelectedUserId,
  } = useCompanyUser();

  const targetUser = isAdminView
    ? companyUsers?.find(u => u.id === userId)
    : user;

  // Verificar se os dados estão carregando corretamente
  const transactions = isAdminView
    ? targetUser?.transactions || []
    : payments.transactions || [];

  const cardsData = isAdminView
    ? {
        balance: targetUser?.balance ?? 0,
        totalTransactions:
          targetUser?.financialCardsData?.totalTransactions ?? 0,
        currentMonthCredits:
          targetUser?.financialCardsData?.currentMonthCredits ?? 0,
        currentMonthSpent:
          targetUser?.financialCardsData?.currentMonthSpent ?? 0,
      }
    : payments.cardsData;

  const financialHistory = isAdminView
    ? targetUser?.financialHistory || []
    : payments.financialHistoryData || [];

  const loadingHistory = isAdminView
    ? loadingUserHistory
    : payments.loadingHistory;
  const isFetching = isAdminView
    ? loadingUserTransactions
    : payments.isFetching;
  const isFetchingNextPage = isAdminView ? false : payments.isFetchingNextPage;
  const hasNextPage = isAdminView ? false : payments.hasNextPage;
  const handleFetchNextPage = isAdminView
    ? () => {}
    : payments.handleFetchNextPage;

  const theme = useTheme();

  // Calcular dados do mês atual
  const currentMonth = new Date().getMonth();
  const currentYear = new Date().getFullYear();

  const currentMonthTransactions = transactions.filter(transaction => {
    const transactionDate = new Date(transaction.createdAt);
    return (
      transactionDate.getMonth() === currentMonth &&
      transactionDate.getFullYear() === currentYear
    );
  });

  const currentMonthDebits = currentMonthTransactions
    .filter(t => t.transactionType === TransactionType.DEBIT)
    .reduce((sum, t) => sum + t.amount, 0);

  const currentMonthCredits = currentMonthTransactions
    .filter(t => t.transactionType === TransactionType.CREDIT)
    .reduce((sum, t) => sum + t.amount, 0);

  const currentMonthNet = currentMonthDebits - currentMonthCredits;

  const handleTransactionDetails = (transaction: ITransactionViewModel) => {
    setCurrentTransaction(transaction);
    setShowTransactionDetails(true);
  };

  // Função removida para visualização de admin - apenas usuários podem gerar faturas
  const handleGenerateInvoice = () => {
    setShowInvoiceModal(true);
  };

  const config = {
    data: financialHistory || [],
    xField: 'data',
    legend: true,
    responsive: true,
    autoFit: true,
    children: [
      {
        type: 'area',
        yField: 'debitos',
        shapeField: 'smooth',
        style: {
          fill: 'rgba(91, 143, 249, 0.4)',
          stroke: '#5B8FF9',
          lineWidth: 2,
        },
        label: {
          style: { dy: -30, dx: -30, textAlign: 'middle' },
          text: (datum: any) => `R$${datum.debitos}`,
        },
        axis: {
          y: {
            title: 'Débitos',
            style: { titleFill: '#5B8FF9' },
          },
        },
      },
      {
        type: 'area',
        yField: 'creditos',
        shapeField: 'smooth',
        style: {
          fill: 'rgba(90, 216, 166, 0.4)',
          stroke: '#5AD8A6',
          lineWidth: 2,
        },
        label: {
          style: { dy: -20, dx: -30, textAlign: 'middle' },
          text: (datum: any) => `R$${datum.creditos}`,
        },
        axis: {
          y: {
            position: 'right',
            title: 'Créditos',
            style: { titleFill: '#5AD8A6' },
          },
        },
      },
    ],
  };

  useEffect(() => {
    if (isAdminView && userId) {
      setSelectedUserId(userId);
    }
  }, [isAdminView, userId, setSelectedUserId]);

  return (
    <>
      <AppBar />
      <Container sx={{ minWidth: '90vw', minHeight: '100vh', pt: 10, pb: 4 }}>
        <Paper
          sx={{
            p: 4,
            mb: 3,
            borderRadius: '12px',
            boxShadow: '0 4px 12px rgba(0,0,0,0.05)',
            width: '100%',
          }}
        >
          <Grid2
            container
            justifyContent="space-between"
            alignItems="flex-start"
          >
            <Box>
              <Typography
                component="p"
                variant="h4"
                color="primary"
                sx={{
                  fontWeight: 600,
                  position: 'relative',
                  marginBottom: '8px',
                  '&:after': {
                    content: '""',
                    position: 'absolute',
                    bottom: -8,
                    left: 0,
                    width: 60,
                    height: 3,
                    backgroundColor: theme.palette.primary.main,
                    borderRadius: 4,
                  },
                }}
              >
                Balanço Financeiro
              </Typography>
              {isAdminView && targetUser && (
                <Typography
                  variant="body1"
                  color="text.secondary"
                  sx={{ mb: 2 }}
                >
                  {targetUser.name}
                </Typography>
              )}
            </Box>
          </Grid2>

          <CardsSection>
            <KpiCard
              title="Consumo do Mês"
              value={currentMonthDebits / 100}
              currency
              icon={<WalletOutlinedIcon />}
            />
            <KpiCard
              title="Transações do Mês"
              value={currentMonthTransactions.length}
              icon={<TuneIcon />}
              backgroundColor="#af1a58"
            />
          </CardsSection>
        </Paper>

        <ContentGrid>
          <MainContent>
            <ChartSection>
              <h3>Total de transações por mês</h3>
              <div className="chart-container">
                {!loadingHistory ? (
                  financialHistory && financialHistory.length > 0 ? (
                    <DualAxes {...config} />
                  ) : (
                    <Empty
                      description={
                        isAdminView
                          ? 'Nenhum dado encontrado para este usuário.'
                          : 'Nenhum dado encontrado.'
                      }
                      image={Empty.PRESENTED_IMAGE_SIMPLE}
                    />
                  )
                ) : (
                  <Skeleton
                    sx={{ height: 500 }}
                    animation="wave"
                    variant="rectangular"
                  />
                )}
              </div>
            </ChartSection>
          </MainContent>

          <SidebarContent>
            <TransactionSidebar
              transactions={transactions}
              onTransactionClick={handleTransactionDetails}
              onLoadMore={handleFetchNextPage}
              hasNextPage={hasNextPage}
              isLoadingMore={isFetchingNextPage}
              isFetching={isFetching}
            />
          </SidebarContent>
        </ContentGrid>

        {/* Seção de Faturas */}
        <Paper
          sx={{
            p: 4,
            mt: 3,
            borderRadius: '12px',
            boxShadow: '0 4px 12px rgba(0,0,0,0.05)',
            width: '100%',
          }}
        >
          <InvoiceList
            userId={targetUser?.id || user?.id || ''}
            isAdmin={isAdmin}
          />
        </Paper>
      </Container>

      {/* Modais existentes */}
      {showTransactionDetails && currentTransaction && (
        <TransactionDetailsModal
          transaction={currentTransaction}
          open={showTransactionDetails}
          onClose={() => setShowTransactionDetails(false)}
        />
      )}
    </>
  );
};

export default Balance;
